// API and mobile use the same filters. Bump the version when formulas or filters change.
export const ACTIVITY_VERSION = "gps-walk-3";
export const ACTIVITY_TIMEZONE = "Europe/Copenhagen";
export function validActivityProfile(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    && typeof value.strideCm === "number" && Number.isFinite(value.strideCm) && value.strideCm >= 20 && value.strideCm <= 200
    && typeof value.weightKg === "number" && Number.isFinite(value.weightKg) && value.weightKg >= 20 && value.weightKg <= 300;
}
export function distanceKm(first, second) {
  const radians = degrees => degrees * Math.PI / 180;
  const a = Math.sin(radians(second.latitude - first.latitude) / 2) ** 2
    + Math.cos(radians(first.latitude)) * Math.cos(radians(second.latitude)) * Math.sin(radians(second.longitude - first.longitude) / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, a))));
}
// 2024 Adult Compendium walking codes 17151/17152/17170/17190/17200/17220/17230.
// https://pacompendium.com/walking/; intermediate thresholds are app approximations.
export function walkingMet(speedKph) {
  const mph = speedKph / 1.609344;
  if (mph < 2) return 2.3;
  if (mph < 2.5) return 2.8;
  if (mph < 2.8) return 3;
  if (mph < 3.5) return 3.8;
  if (mph < 4) return 4.8;
  if (mph < 4.5) return 5.5;
  return 7;
}
export function emptyActivityBasis() {
  return { walkingKm: 0, activeSeconds: 0, caloriePerKg: 0, observedSegments: 0, usedSegments: 0, ignoredSegments: 0, observedSeconds: 0 };
}
// Consecutive valid route points; connectFromPrevious preserves invalid-report gaps.
export function addActivitySegment(basis, previous, point) {
  const valid = [previous, point].every(p => p.gpsValid === 1 && typeof p.latitude === "number" && typeof p.longitude === "number"
    && Number.isFinite(p.latitude) && Number.isFinite(p.longitude) && Math.abs(p.latitude) <= 90 && Math.abs(p.longitude) <= 180);
  const seconds = (Date.parse(point.receivedAt) - Date.parse(previous.receivedAt)) / 1000;
  const km = valid ? distanceKm(previous, point) : NaN;
  const speed = km / (seconds / 3600);
  if (!valid || !point.connectFromPrevious || !(seconds > 0 && seconds <= 300) || speed > 8
    || (point.speedKph != null && (!Number.isFinite(point.speedKph) || point.speedKph < 0 || point.speedKph > 8))) {
    basis.ignoredSegments++;
    return;
  }
  basis.observedSegments++;
  basis.observedSeconds += seconds;
  if (speed < 0.5) return;
  basis.walkingKm += km;
  basis.activeSeconds += seconds;
  basis.caloriePerKg += walkingMet(speed) * 3.5 / 200 * seconds / 60;
  basis.usedSegments++;
}
export function activityFromBasis(basis, profile) {
  const available = basis.observedSegments > 0;
  const stride = profile?.strideCm, weight = profile?.weightKg;
  return {
    distanceKm: available ? Math.round(basis.walkingKm * 1000) / 1000 : null,
    steps: available && Number.isFinite(stride) && stride >= 20 && stride <= 200 ? Math.round(basis.walkingKm * 100000 / stride) : null,
    calories: available && Number.isFinite(weight) && weight >= 20 && weight <= 300 ? Math.round(basis.caloriePerKg * weight) : null,
    activeMinutes: available ? Math.round(basis.activeSeconds / 60) : null,
    averageSpeedKph: basis.activeSeconds > 0 ? Math.round(basis.walkingKm / (basis.activeSeconds / 3600) * 10) / 10 : null,
    usedSegments: basis.usedSegments, ignoredSegments: basis.ignoredSegments,
  };
}
const dateKeyFormatter = new Intl.DateTimeFormat("en-CA", { timeZone: ACTIVITY_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" });
const midnightHourFormatter = new Intl.DateTimeFormat("en-GB", { timeZone: ACTIVITY_TIMEZONE, hour: "2-digit", hourCycle: "h23" });
export function dateKey(value = new Date()) {
  const parts = dateKeyFormatter.formatToParts(value);
  const part = name => parts.find(p => p.type === name).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}
export function validDateKey(key) {
  if (typeof key !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(key) || key < "1970-01-01" || key > "9998-12-31") return false;
  const date = new Date(`${key}T12:00:00Z`);
  return Number.isFinite(+date) && date.toISOString().slice(0, 10) === key;
}
export function shiftDate(key, days) {
  if (!validDateKey(key)) throw new Error("invalid_activity_period");
  const date = new Date(`${key}T12:00:00Z`); date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
export function activityDayBounds(key) {
  if (!validDateKey(key)) throw new Error("invalid_activity_period");
  const midnight = day => {
    const nominal = Date.parse(`${day}T00:00:00Z`);
    // Copenhagen changes DST after local midnight.
    const hour = Number(midnightHourFormatter.format(new Date(nominal)));
    return new Date(nominal - hour * 3600000).toISOString();
  };
  return { startAt: midnight(key), endAt: midnight(shiftDate(key, 1)) };
}
export function activityPeriod(period, anchor) {
  if (!["day", "week", "month", "year"].includes(period) || !validDateKey(anchor)) throw new Error("invalid_activity_period");
  let startDate = anchor;
  if (period === "week") startDate = shiftDate(anchor, -((new Date(`${anchor}T12:00:00Z`).getUTCDay() + 6) % 7));
  if (period === "month") startDate = `${anchor.slice(0, 7)}-01`;
  if (period === "year") startDate = `${anchor.slice(0, 4)}-01-01`;
  const next = new Date(`${startDate}T12:00:00Z`);
  if (period === "month") next.setUTCMonth(next.getUTCMonth() + 1);
  else if (period === "year") next.setUTCFullYear(next.getUTCFullYear() + 1);
  else next.setUTCDate(next.getUTCDate() + (period === "week" ? 7 : 1));
  const endDate = next.toISOString().slice(0, 10), days = [];
  for (let day = startDate; day < endDate; day = shiftDate(day, 1)) days.push(day);
  return { period, anchor, startDate, endDate, days, timeZone: ACTIVITY_TIMEZONE, weekStartsOn: "monday" };
}
